/**
 * The interface languages, mirrored from the backend's `src/common/languages.ts`.
 * See docs/plans/localization.md. Pure module: no i18next, no browser globals, so
 * anything (reducers, mappings, tests) can import it cheaply.
 *
 * `LanguageIds` in utils/constant.ts is the language being *learned*. Never mix the two.
 */

export const SUPPORTED_LANGUAGES = ['en', 'fr', 'es', 'ja', 'de', 'it', 'pt', 'ko'] as const

export type LanguageCode = (typeof SUPPORTED_LANGUAGES)[number]

/** Every interface language except English, the source and fallback. */
export const LEARNER_LANGUAGES = SUPPORTED_LANGUAGES.filter((code) => code !== 'en') as Exclude<LanguageCode, 'en'>[]

export const DEFAULT_LANGUAGE: LanguageCode = 'en'

/** Each language in its own name, in the order the language setting lists them. */
export const LANGUAGES: { code: LanguageCode; nativeName: string }[] = [
  { code: 'en', nativeName: 'English' },
  { code: 'fr', nativeName: 'Français' },
  { code: 'es', nativeName: 'Español' },
  { code: 'ja', nativeName: '日本語' },
  { code: 'de', nativeName: 'Deutsch' },
  { code: 'it', nativeName: 'Italiano' },
  { code: 'pt', nativeName: 'Português' },
  { code: 'ko', nativeName: '한국어' },
]

/** Development-only pseudo-locale: English, accented and about 35% longer. */
export const PSEUDO_LOCALE = 'en-XA'

/** Where the web remembers the interface language between visits. */
export const LANGUAGE_STORAGE_KEY = 'maayot.lang'

/** Namespaces follow product areas. `common` is the default. */
export const NAMESPACES = [
  'common',
  'auth',
  'onboarding',
  'reader',
  'words',
  'settings',
  'plan',
  'account',
  'corrections',
  'writeSpeak',
  'discussion',
  'dashboard',
  'quiz',
  'referral',
  'landing',
  'offers',
  'errors',
  'emails',
] as const

export type Namespace = (typeof NAMESPACES)[number]

export const DEFAULT_NAMESPACE: Namespace = 'common'

export const isSupportedLanguage = (value: unknown): value is LanguageCode =>
  typeof value === 'string' && (SUPPORTED_LANGUAGES as readonly string[]).includes(value)

/**
 * 'fr-CA' -> 'fr', 'PT_br' -> 'pt', 'zh' -> null. Bare ISO 639-1 codes only, so any region
 * or script subtag is dropped. Returns null for anything we do not support.
 */
export const toSupportedLanguage = (input: unknown): LanguageCode | null => {
  if (typeof input !== 'string') return null
  const base = input.trim().toLowerCase().split(/[-_]/)[0]
  return isSupportedLanguage(base) ? base : null
}

/** Like toSupportedLanguage, but an unsupported or empty value falls back to English. */
export const normalizeLanguage = (input: unknown): LanguageCode => toSupportedLanguage(input) || DEFAULT_LANGUAGE

export const nativeLanguageName = (code: LanguageCode): string =>
  LANGUAGES.find((language) => language.code === code)?.nativeName || code

/** How `native_language` was set: saved from the browser on its own, or picked by the learner. */
export type NativeLanguageSource = 'detected' | 'chosen'

export const toNativeLanguageSource = (value: unknown): NativeLanguageSource | null =>
  value === 'detected' || value === 'chosen' ? value : null

/**
 * The language fields of a profile or login response's `customFields`, cleaned up for the
 * store: an unsupported or missing `native_language` reads as null ("not chosen yet"), and an
 * unknown `native_language_source` as null (a language saved before the field existed).
 */
export const profileLanguageFields = (customFields: any) => ({
  native_language: toSupportedLanguage(customFields?.native_language),
  native_language_source: toNativeLanguageSource(customFields?.native_language_source),
  show_english_meanings: customFields?.show_english_meanings === true,
})
